import React from 'react';
import moment from 'moment';
import Select from 'react-select';
import Card from 'material-ui/Card/Card';
import CardHeader from 'material-ui/Card/CardHeader';
import FlatButton from 'material-ui/FlatButton';
import Theme from 'styles/theme';
import TwoLineChart from 'components/pure/charts/TwoLineChart';
import TrendPivotChartActions from 'actions/calls/trend/TrendPivotChartActions';
import IconButton from 'components/pure/utils/IconButton';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';

require('styles/calls/TrendPivotChart.styl');

class TrendPivotChart extends React.Component {
  constructor() {
    super();
    this.state = {
      pivot: null,
    };
    this.backwardMaxDate = this.backwardMaxDate.bind(this);
    this.forwardMaxDate = this.forwardMaxDate.bind(this);
    this.onPivotSelectOptionChange = this.onPivotSelectOptionChange.bind(this);
  }

  _pivotOptions() {
    const state = this.props.trendPivotState;
    return state.fields.map((_, field) => ({label: field, value: field})).toArray();
  }

  shouldRenderPivotDropdown() {
    // If the user selects only 1-search-field, then we allow the user to click 'Trend' to show the trend chart but not
    // allowing them to pivot
    return this._pivotOptions().length > 1;
  }

  pivotSelectOptions() {
    return this._pivotOptions();
  }

  onPivotSelectOptionChange(pivot) {
    this.setState({pivot});
    if (pivot?.value) {
      TrendPivotChartActions.searchPivot(pivot.value);
    } else {
      TrendPivotChartActions.clearPivotState();
      TrendPivotChartActions.searchTrend();
    }
  }

  backwardMaxDate() {
    TrendPivotChartActions.switchSearchMaxDate.defer('backward');
  }

  forwardMaxDate() {
    TrendPivotChartActions.switchSearchMaxDate.defer('forward');
  }

  static trendSpanMessage(pivotField, trendValue) {
    return pivotField ? `With ${pivotField} Filter (${trendValue} calls)` : `${trendValue} calls`;
  }

  renderCardHeaderContent() {
    const {trend, pivot} = this._trendPivotFromProps();
    const pivotField = pivot.pivot;
    // card-header consists of Pivot label, pivot dropdown, pivot call counts, and 'Download PDF' button

    return (
      <section>
        {this.props.trendPivotState.loading && <LoadingOverlay label="Calculating Trend" />}
        <div className="trend-pivot-card-header">
          {this.shouldRenderPivotDropdown() && (
            <div style={{display: 'flex'}}>
              <div className="trend-pivot-card-header-label">
                <span>Pivot</span>
                <IconButton
                  className="visibility"
                  tooltipPosition="top-center"
                  tooltip={
                    <div style={{whiteSpace: 'normal'}}>
                      The pivot feature allows you to split the trend of your search results by a
                      filter in your search. For example, if your search were to include a Duration
                      filter, setting pivot to Duration will show you the trend of results both with
                      and without the Duration filter applied.
                    </div>
                  }>
                  <i className="material-icons">info</i>
                </IconButton>
              </div>
              <div className="trend-pivot-card-header-pivot-dropdown">
                <Select
                  multi={false}
                  searchable={false}
                  options={this.pivotSelectOptions()}
                  placeholder={`- no pivot -`}
                  className="trend-pivot-card-header-pivot-dropdown-select"
                  onChange={this.onPivotSelectOptionChange}
                  value={this.state.pivot}
                />
              </div>
            </div>
          )}
          <div className="trend-pivot-line-labels">
            {trend && (
              <div>
                <i className="material-icons icon trend-results-total-icon">remove</i>
                <span className="trend-results-total-label">
                  {TrendPivotChart.trendSpanMessage(pivotField, trend.total)}
                </span>
              </div>
            )}
            {pivot && pivotField && (
              <div>
                <i className="material-icons icon pivot-results-total-icon">remove</i>
                <span className="pivot-results-total-label">
                  Without {pivotField} Filter ({pivot.total} calls)
                </span>
              </div>
            )}
          </div>
          {/* XXX: Not working -- Saves only blank PDFs */}
          {/* <div className='trend-pivot-card-header-download-pdf ignore-pdf'> */}
          {/* <FlatButton label={`Download PDF`} backgroundColor={Theme.palette.lynch} */}
          {/* onClick={::this.saveTrendPivotChartAsPDF}/> */}
          {/* </div> */}
        </div>
      </section>
    );
  }

  renderTrendChart() {
    const state = this.props.trendPivotState;
    const trend = state.trend.buckets.toArray();
    const pivot = state.pivot.buckets.toArray();
    const switchDatesBackgroundColor = Theme.palette.lynch;
    return (
      <div className="line-chart-container">
        <TwoLineChart
          width={`95%`}
          height={250}
          line1={trend}
          line2={pivot}
          line2Field={state.pivot.pivot}
          line1MessageFunc={TrendPivotChart.trendSpanMessage}
          popoverContent={TrendPivotChart._trendChartPopoverContent}
        />
        <div className="trend-pivot-card-switch-dates">
          <div className="trend-pivot-card-switch-dates-previous">
            <FlatButton
              onClick={this.backwardMaxDate}
              disabled={state.loading}
              icon={<i className="material-icons icon">chevron_left</i>}
              backgroundColor={switchDatesBackgroundColor}
            />
          </div>
          <div className="trend-pivot-card-switch-dates-next" data-direction="forward">
            <FlatButton
              onClick={this.forwardMaxDate}
              disabled={state.loading}
              icon={<i className="material-icons icon">chevron_right</i>}
              backgroundColor={switchDatesBackgroundColor}
            />
          </div>
        </div>
      </div>
    );
  }

  static _trendChartPopoverContent(
    line1,
    line1SpanMessage,
    line2SpanMessage,
    line2Field,
    percentageDiff
  ) {
    const header = 'Week starting ' + moment(line1.date).format('MM/DD/YYYY');

    return (
      <div className="trend-pivot-chart-popover-container">
        <div className="trend-pivot-chart-popover-header">
          <strong>{header}</strong>
        </div>
        <div className="trend-pivot-chart-popover-icons">
          <i key="trend-i" className="material-icons icon trend-results-total-icon">
            remove
          </i>
          <span key="trend-total" className="trend-results-total-label">
            {line1SpanMessage}
          </span>
        </div>
        {line2Field && (
          <div className="trend-pivot-chart-popover-icons">
            <i key="trend-i" className="material-icons icon pivot-results-total-icon">
              remove
            </i>
            <span key="trend-total" className="trend-results-total-label">
              {line2SpanMessage}
            </span>
          </div>
        )}
        {line2Field && (
          <div className="trend-pivot-chart-percentage-diff">
            Percentage difference: {(percentageDiff * 100).toFixed(2)}%
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div className="trend-pivot">
        <Card className="call trend-pivot-card" expanded={true}>
          <CardHeader>{this.renderCardHeaderContent()}</CardHeader>
          {this.renderTrendChart()}
        </Card>
      </div>
    );
  }

  _trendPivotFromProps() {
    const {trend, pivot} = this.props.trendPivotState;
    return {trend, pivot};
  }
}

export default TrendPivotChart;
